import { useEffect, useRef } from 'react';
import { useCurrentRefinements, usePagination, useSortBy } from 'react-instantsearch';

import { areFacetsEqual } from './compareCurrentRefinements';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useAlgoliaSortingOptions } from '~/utilities/hooks/search/useAlgoliaSortingOptions';

// all this logic is required because algolia library doesn't provide currently rendered pages in infinite hits component
export const useCustomPagination = () => {
  const { items: appliedFilters } = useCurrentRefinements();
  const { sortingOptions } = useAlgoliaSortingOptions();
  const { currentRefinement: appliedSorting } = useSortBy({ items: sortingOptions });
  const { categoryDetails } = useProductListContext();
  const { currentRefinement: currentPage, refine: setCurrentPage } = usePagination();

  const renderedPages = useRef<Array<number>>([currentPage]);
  const previousCategoryCode = useRef(categoryDetails?.categoryCode);
  const previousSortBy = useRef(appliedSorting);
  const previousRefinements = useRef(appliedFilters);

  const currentPages = renderedPages.current;

  const prevPage = currentPages.at(0)!;
  const nextPage = currentPages.at(-1)! + 2;

  const nextSearchParams = new URLSearchParams(window.location.search);
  const prevSearchParams = new URLSearchParams(window.location.search);

  const calculatedPrevPage = Math.max(prevPage, 0);

  if (calculatedPrevPage > 1) {
    prevSearchParams.set('page', `${calculatedPrevPage}`);
  } else {
    prevSearchParams.delete('page');
  }

  nextSearchParams.set('page', `${nextPage}`);

  // required to reset rendered pages when category code changes (upcoming nextjs link navigation)
  useEffect(() => {
    if (previousCategoryCode.current !== categoryDetails?.categoryCode) {
      renderedPages.current = [];
    }

    previousCategoryCode.current = categoryDetails?.categoryCode;
  }, [categoryDetails?.categoryCode]);

  // required to reset rendered pages when sorting changes
  useEffect(() => {
    if (previousSortBy.current !== appliedSorting) {
      renderedPages.current = [];
    }

    previousSortBy.current = appliedSorting;
  }, [appliedSorting]);

  // required to reset rendered pages when filters changes
  useEffect(() => {
    if (!areFacetsEqual(previousRefinements.current, appliedFilters)) {
      renderedPages.current = [];
    }

    previousRefinements.current = appliedFilters;
  }, [appliedFilters]);

  // tracks rendered pages
  useEffect(() => {
    if (!renderedPages.current.includes(currentPage)) {
      renderedPages.current.push(currentPage);
      renderedPages.current.sort((a, b) => a - b);
    }
  }, [currentPage]);

  return {
    setCurrentPage,
    currentPages,
    currentPage,
    nextHref: `${window.location.pathname}?${nextSearchParams.toString()}`,
    prevHref: `${window.location.pathname}${
      prevSearchParams.size ? `?${prevSearchParams.toString()}` : ''
    }`,
    nextPage,
    prevPage,
  };
};
